import {
  IEditedAnswer,
  ISurveyOptionForEdit,
  ISurveyQuestionForEdit,
  IUpdateSurveyQuestionAnswer,
} from "../../../@types/admin/AdminSurvey";

/**
 * Conditional question visibility, ported from Retool's
 * `moduleSurveysProms/lib/visibleQuestions.js` (EN-5164). The ticket calls
 * this out as must-port: it decides which questions the answer editor shows,
 * and a survey's branching is what makes an answer set meaningful.
 *
 * The rule is a walk, not a filter. Starting at the lowest-ordered question,
 * each step picks the next question from, in order of precedence:
 *
 *   1. the `nextQuestionId` of the option currently selected for it,
 *   2. the question's own unconditional `nextQuestionId`,
 *   3. the next question by `order`.
 *
 * Questions never reached by that walk are not shown and are not answerable.
 * Whatever the operator has edited counts as "currently selected", so
 * changing a branching answer re-routes the rest of the form immediately.
 */

/**
 * The option currently chosen for a question — the pending edit if there is
 * one, otherwise the stored answer.
 *
 * Retool checked the stored answer two ways: by `selectedOptionIds`, and by
 * matching `answerValue` against the options' `value`. Both are kept; the
 * second is how a slider or a value-typed answer resolves to an option.
 */
export function getSelectedOption(
  question: ISurveyQuestionForEdit,
  editedAnswers: Record<string, IEditedAnswer>
): ISurveyOptionForEdit | null {
  const options = question.options ?? [];
  if (options.length === 0) return null;

  // A pending edit supersedes the stored answer entirely. Falling through to
  // the stored answer when the edit carries no selected option would keep the
  // OLD branch active after the operator deselects the last option of a
  // multi-select or moves a slider (whose edits carry `answerValue` with
  // `surveyQuestionOptionIds: null`).
  const edited = editedAnswers[question.id];
  if (edited) {
    const ids = edited.surveyQuestionOptionIds;
    if (ids != null) {
      return options.find((option) => ids.includes(option.id)) ?? null;
    }
    if (edited.answerValue != null && edited.answerValue !== "") {
      const value = edited.answerValue;
      return (
        options.find((option) => String(option.value) === String(value)) ?? null
      );
    }
    return null;
  }

  const current = question.currentAnswer;
  if (!current) return null;

  if (current.selectedOptionIds?.length) {
    const ids = current.selectedOptionIds;
    return options.find((option) => ids.includes(option.id)) ?? null;
  }

  if (current.answerValue != null) {
    return (
      options.find(
        (option) => String(option.value) === String(current.answerValue)
      ) ?? null
    );
  }

  return null;
}

/**
 * The questions reachable from the first one, in walk order.
 *
 * The `seen` guard is Retool's and is load-bearing: a survey whose branches
 * point back at an earlier question would otherwise loop forever. It stops
 * the walk rather than repeating a question.
 */
export function getVisibleQuestions(
  questions: ISurveyQuestionForEdit[],
  editedAnswers: Record<string, IEditedAnswer>
): ISurveyQuestionForEdit[] {
  if (questions.length === 0) return [];

  const sorted = [...questions].sort((a, b) => a.order - b.order);
  // Explicit type parameters: inferring from the tuple gives
  // `Map<string | ISurveyQuestionForEdit, …>`, and `byId.get()` then widens
  // the loop variable enough to break its own inference.
  const byId = new Map<string, ISurveyQuestionForEdit>(
    questions.map((item) => [item.id, item])
  );

  const visible = new Set<string>();
  let current: ISurveyQuestionForEdit | undefined = sorted[0];

  while (current && !visible.has(current.id)) {
    // Bound to a const so the fallback lookup below doesn't close over the
    // loop variable.
    const step: ISurveyQuestionForEdit = current;
    visible.add(step.id);

    const selected = getSelectedOption(step, editedAnswers);
    let nextId = selected?.nextQuestionId ?? null;

    if (!nextId) nextId = step.nextQuestionId ?? null;

    if (!nextId) {
      const index = sorted.findIndex((q) => q.id === step.id);
      nextId = sorted[index + 1]?.id ?? null;
    }

    current = nextId ? byId.get(nextId) : undefined;
  }

  // Retool returned them in the source order, not the walk order — kept, so
  // the editor reads top to bottom like the survey does.
  return questions.filter((question) => visible.has(question.id));
}

/**
 * The PUT body's `answers`, ported from the inline expression in Retool's
 * `UpdateSurveyAnswers` body.
 *
 * Every **visible** question is sent, edited or not: an edited one carries the
 * pending value, an untouched one re-sends what it already had. Sending only
 * the edited ones would be read as clearing the rest.
 *
 * ⚠️ Built from the visible questions, not all of them. A question the walk
 * doesn't reach was never answerable, and posting an answer for it would
 * write a value the patient was never asked for.
 */
export function buildAnswersPayload(
  visibleQuestions: ISurveyQuestionForEdit[],
  editedAnswers: Record<string, IEditedAnswer>
): IUpdateSurveyQuestionAnswer[] {
  return visibleQuestions.map((question) => {
    const edited = editedAnswers[question.id];
    if (edited) {
      return {
        surveyQuestionId: question.id,
        answerValue: edited.answerValue ?? null,
        surveyQuestionOptionIds: edited.surveyQuestionOptionIds ?? null,
      };
    }
    return {
      surveyQuestionId: question.id,
      answerValue: question.currentAnswer?.answerValue ?? null,
      surveyQuestionOptionIds:
        question.currentAnswer?.selectedOptionIds ?? null,
    };
  });
}

/** The value to show in a question's control: pending edit, else stored. */
export function getAnswerValue(
  question: ISurveyQuestionForEdit,
  editedAnswers: Record<string, IEditedAnswer>
): string {
  const edited = editedAnswers[question.id];
  if (edited && edited.answerValue !== undefined) {
    return edited.answerValue ?? "";
  }
  return question.currentAnswer?.answerValue ?? "";
}

/** The option ids to show as selected: pending edit, else stored. */
export function getSelectedOptionIds(
  question: ISurveyQuestionForEdit,
  editedAnswers: Record<string, IEditedAnswer>
): string[] {
  const edited = editedAnswers[question.id];
  if (edited && edited.surveyQuestionOptionIds !== undefined) {
    return edited.surveyQuestionOptionIds ?? [];
  }
  return question.currentAnswer?.selectedOptionIds ?? [];
}
